<template>
  <div>

    <div class="profit-header">
      <strong>Total Profit: {{ totalProfit.toFixed(2) }}</strong>
      <span class="profit-count">{{ profitEvents.length }} deals</span>
    </div>

    <div class="tabular-header custom-grid-profitsummary">
      <div>Time</div>
      <div>Event</div>
      <div>P&L</div>
      <div>Margin</div>
      <div>Accumulated</div>
    </div>

    <div class="profit-summary scrollable">
      <div v-for="(event, index) in profitEvents" :key="`${event.timestamp}-${index}`" class="tabular-row custom-grid-profitsummary">
        <div>{{ formatRelativeTime(event.orderData.timestamp) }}</div>
        <div><span :class="['event-badge', 'event-' + canonicalPurpose(event.purpose)]">{{ canonicalPurpose(event.purpose) }}</span></div>
        <div :class="event.metrics.pnl >= 0 ? 'profit-positive' : 'profit-negative'">
          {{ event.metrics.pnl >= 0 ? '+' : '' }}{{ event.metrics.pnl.toFixed(3) }}
        </div>
        <div :class="marginClass(event.marginPct)">{{ formatMargin(event.marginPct) }}</div>
        <div class="accumulated">{{ event.accumulated.toFixed(4) }}</div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex';
import { formatRelativeTime } from '../utils/funcs.js'
import { CLOSE_PURPOSES, canonicalPurpose, readOrderNotional } from '../utils/manualChartDays.js';

function exitMarginPct(event) {
  if (!CLOSE_PURPOSES.has(event?.purpose)) return null;
  const notional = readOrderNotional(event);
  if (!(notional > 0)) return null;
  const pnl = Number(event?.metrics?.pnl);
  if (!Number.isFinite(pnl)) return null;
  return (pnl / notional) * 100;
}

export default {
  name: "profitSummary",
  computed: {
    ...mapState({
        eventLogs: state => state.eventLogs || [],
    }),

    profitEvents() {
      // Filtrar eventos que têm metrics.pnl
      const filtered = this.eventLogs
        .filter(event => event.metrics && event.metrics.pnl != null && event.metrics.pnl !== 0)
        .sort((a, b) => b.timestamp - a.timestamp); // Mais recente primeiro

      // Calcular acumulado (do mais antigo para o mais recente)
      let accumulated = 0;
      const reversed = [...filtered];

      return reversed.map(event => {
        accumulated += event.metrics.pnl;
        return {
          ...event,
          accumulated,
          marginPct: exitMarginPct(event),
        };
      }).reverse(); // Voltar para ordem decrescente (mais recente primeiro)
    },

    totalProfit() {
      return this.profitEvents.reduce((sum, event) => sum + event.metrics.pnl, 0);
    }
  },

  methods: {
    canonicalPurpose,
    formatRelativeTime,
    formatMargin(value) {
      if (value == null || !Number.isFinite(value)) return '—';
      const sign = value > 0 ? '+' : '';
      return `${sign}${value.toFixed(2)}%`;
    },
    marginClass(value) {
      if (value == null || !Number.isFinite(value)) return 'margin-empty';
      if (value > 0) return 'profit-positive';
      if (value < 0) return 'profit-negative';
      return 'margin-empty';
    },
  }
};
</script>

<style>

.profit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: var(--table-header-bgcolor);
  border-radius: 10px 10px 0 0;
  margin-bottom: 2px;
}

.profit-header strong {
  font-size: 1.1em;
  color: var(--text-color);
}

.profit-count {
  font-size: 0.85em;
  color: var(--text-color);
  opacity: 0.7;
}

.profit-summary {
  background-color: var(--table-header-bgcolor);
  border-radius: 0 0 10px 10px;
  height: 570px;
}

.custom-grid-profitsummary {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr 0.72fr 0.68fr 0.8fr;
  gap: 6px;
  font-size: 0.82rem;
}

.custom-grid-profitsummary .event-badge {
  padding: 2px 5px;
  white-space: nowrap;
}

.margin-empty {
  color: var(--text-color);
  opacity: 0.45;
}

.profit-positive {
  color: #4ade80;
  font-weight: 500;
}

.profit-negative {
  color: #f87171;
  font-weight: 500;
}

.accumulated {
  font-weight: 600;
  color: var(--text-color);
}

.event-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: 500;
}

.event-regularSell {
  background-color: rgba(255, 206, 86, 0.2);
  color: rgba(255, 206, 86, 1);
  border: 1px solid rgba(255, 206, 86, 0.4);
}

.event-rebuyBuy {
  background-color: rgba(97, 237, 99, 0.2);
  color: rgb(4, 160, 35);
  border: 1px solid rgba(97, 237, 99, 0.2);
}

.event-manualSell,
.event-closeLong {
  background-color: rgba(78, 113, 209, 0.2);
  color: rgb(112, 140, 219);
  border: 1px solid rgba(135, 169, 107, 0.4);
}

.event-hedgeBuy,
.event-closeShort {
  background-color: rgba(245, 158, 11, 0.15);
  color: rgb(251, 191, 36);
  border: 1px solid rgba(245, 158, 11, 0.3);
}

</style>
